import {Grid, IconButton, Table, TableCell, Tooltip, Typography} from "@mui/material";
import React from "react";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import {SortableContainer, SortableElement, SortableHandle} from "react-sortable-hoc";
import EnhancedAutocomplete from "../../../../../components/EnhancedAutocomplete";
import {Drag} from "../../../../../utils/icons";
import {Create, Delete} from "@mui/icons-material";
import EnhancedMappingEditor from "./EnhancedMappingEditor";
import {parseCondition} from "../../../../inventory/shared/mapping_templates";
import {Field, FormSpy} from "react-final-form";
import {isValidExpression} from "../../../../inventory/shared/mapping_checker";
import { pageContentGap } from '../../../../../theme';

// region TypeScript Interfaces
interface MappingItem {
    field?: string;
    alias?: string;
    SourceField?: string;
    TargetField?: string;
    Condition?: unknown;
    index?: number;
    [key: string]: unknown;
}

interface MappingOption {
    value: string;
    label: string;
    [key: string]: unknown;
}

interface SortableItemProps {
    item: MappingItem & { index: number };
    targetOptions: MappingOption[];
    sourceOptions: MappingOption[];
    type: string;
    rowActions?: (item: MappingItem) => React.ReactNode;
    onItemSelect: (index: number) => void;
    onItemDelete: (index: number) => void;
    onTargetUpdate: (index: number, value: string) => void;
    onSourceUpdate: (index: number, value: string) => void;
    onConditionUpdate?: (index: number, condition: unknown) => void;
    swapTargetSource?: boolean;
    disableOrdering?: boolean;
    extraRows?: (item: MappingItem) => React.ReactNode;
}

interface SortableListProps {
    items: MappingItem[];
    targetOptions: MappingOption[];
    sourceOptions: MappingOption[];
    type: string;
    rowActions?: (item: MappingItem) => React.ReactNode;
    onItemSelect: (index: number) => void;
    onItemDelete: (index: number) => void;
    onTargetUpdate: (index: number, value: string) => void;
    onSourceUpdate: (index: number, value: string) => void;
    onConditionUpdate?: (index: number, condition: unknown) => void;
    swapTargetSource?: boolean;
    disableOrdering?: boolean;
    extraRows?: (item: MappingItem) => React.ReactNode;
}

interface EnhancedMappingTableProps {
    type: string;
    headers: React.ReactNode;
    actions?: React.ReactNode;
    rowActions?: (item: MappingItem) => React.ReactNode;
    items: MappingItem[];
    targetOptions: MappingOption[];
    sourceOptions: MappingOption[];
    onSortEnd: (sort: {oldIndex: number; newIndex: number}) => void;
    onItemDelete: (index: number) => void;
    onTargetUpdate: (index: number, value: string) => void;
    onSourceUpdate: (index: number, value: string) => void;
    onConditionUpdate?: (index: number, condition: unknown) => void;
    swapTargetSource?: boolean;
    disableOrdering?: boolean;
    extraRows?: (item: MappingItem) => React.ReactNode;
    conditionaEditorTitle?: string;
}
// endregion

const iconButtonSx = (theme: any) => ({
    backgroundColor: "white",
    border: "none",
    borderRadius: "6px",
    "& svg": { color: "rgba(0, 0, 0, 0.54)" }
});
const Draggable = SortableHandle(({children, style}: {children?: React.ReactNode; style?: React.CSSProperties}) => (
    <div style={{cursor: "move", ...style}}>{children}</div>
)) as React.ComponentType<{children?: React.ReactNode; style?: React.CSSProperties}>;
const SortableItem = SortableElement((props: SortableItemProps) => {
    const {item, targetOptions, sourceOptions, type} = props;
    const {rowActions} = props;
    const {onItemSelect, onItemDelete, onTargetUpdate, onSourceUpdate} = props;
    const {onConditionUpdate} = props;
    let target = "";
    let source = "";
    if (type === "rules") {
        target = item.TargetField || "";
        source = item.SourceField || "";
    } else {
        target = item.alias || "";
        source = item.field || "";
    }
    let inputs = [
        <TableCell key={"targetInput"}>
            <EnhancedAutocomplete
                notMultiple={true}
                promptDisabled={true}
                value={target || null}
                options={targetOptions.map(option => option.value)}
                onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                }}
                onChange={(e: any, value: string) => {
                    onTargetUpdate(item.index, value);
                }}
            />
        </TableCell>,
        <TableCell key={"sourceInput"}>
            <EnhancedAutocomplete
                notMultiple={true}
                promptDisabled={true}
                value={source || null}
                error={
                    (source !== undefined && source !== null && source !== "")
                        ? isValidExpression(source, sourceOptions.map(option => option.value)) ? undefined : true
                        : undefined
                }
                helperText={
                    (source !== undefined && source !== null && source !== "")
                        ? isValidExpression(source, sourceOptions.map(option => option.value)) ? "" : "This expression is invalid."
                        : ""
                }
                options={sourceOptions.map(option => option.value)}
                onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                }}
                onChange={(e: any, value: string) => {
                    onSourceUpdate(item.index, value);
                }}
            />
        </TableCell>
    ]
    if (props.swapTargetSource) inputs.reverse();
    return (
        <TableRow style={{backgroundColor: "white",
    border: "none", cursor: onConditionUpdate !== undefined ? "pointer" : "default"}}
                  onClick={(e) => {
                      if (onConditionUpdate !== undefined) {
                          onItemSelect(props.item.index)
                      }
                  }}
        >
            {!props.disableOrdering && (
                <>
                    <TableCell style={{padding: "0 0 0 12px"}}>
                        <Draggable>
                            <Drag/>
                        </Draggable>
                    </TableCell>
                    <TableCell style={{padding: 0}}>
                        {item.index + 1}
                    </TableCell>
                </>
            )}
            {inputs}
            {props.extraRows !== undefined && props.extraRows(item)}
            {onConditionUpdate !== undefined && (
                <TableCell>
                    <Typography>
                        {parseCondition(item.Condition, sourceOptions)}
                    </Typography>
                </TableCell>
            )}
            <TableCell style={{display: "flex", justifyContent: "flex-end"}} id={"delete_" + type}>
                {rowActions !== undefined && rowActions(item)}
                {onConditionUpdate !== undefined && (
                    <Tooltip title={"Edit Condition"}>
                        <IconButton
                            sx={iconButtonSx}
                            onClick={(e) => {
                                e.stopPropagation();
                                onItemSelect(props.item.index)
                            }}
                            size="large">
                            <Create/>
                        </IconButton>
                    </Tooltip>
                )}
                <Tooltip title={"Delete"}>
                    <IconButton
                        sx={iconButtonSx}
                        onClick={(e) => {
                            e.stopPropagation();
                            onItemDelete(props.item.index)
                        }}
                        size="large">
                        <Delete/>
                    </IconButton>
                </Tooltip>
            </TableCell>
        </TableRow>
    );
}) as React.ComponentType<SortableItemProps & {index: number}>;
const SortableList = SortableContainer((props: SortableListProps) => {
    const {type, disableOrdering} = props;
    const {items, targetOptions, sourceOptions} = props;
    const {rowActions} = props;
    const {onItemSelect, onItemDelete, onTargetUpdate, onSourceUpdate} = props;
    const {onConditionUpdate} = props;
    return (
        <TableBody id={"exportTable" + props.type}>
            {items.map((item, index) => {
                return (
                    <SortableItem
                        swapTargetSource={props.swapTargetSource}
                        key={`item-${index}`} index={index}
                        type={type} disableOrdering={disableOrdering}
                        item={{...item, index: index}}
                        targetOptions={targetOptions}
                        sourceOptions={sourceOptions}
                        extraRows={props.extraRows}
                        rowActions={rowActions}
                        onConditionUpdate={onConditionUpdate}
                        onItemSelect={onItemSelect}
                        onItemDelete={onItemDelete}
                        onTargetUpdate={onTargetUpdate}
                        onSourceUpdate={onSourceUpdate}
                    />
                )
            })}
        </TableBody>
    );
}) as React.ComponentType<SortableListProps & {
    helperClass?: string;
    helperContainer?: () => HTMLElement | null;
    useDragHandle?: boolean;
    onSortEnd: (sort: {oldIndex: number; newIndex: number}) => void;
    onSortStart?: (info: {node: any; helper: any}) => void;
}>;
export default function EnhancedMappingTable(props: EnhancedMappingTableProps) {
    const {onSortEnd, onItemDelete, onTargetUpdate, onSourceUpdate} = props;
    const {onConditionUpdate} = props;
    const {type} = props;
    const {headers, actions} = props;
    const {rowActions} = props;
    const {
        items,
        targetOptions, // Expected to be in the form of [ { value: x, label: y } ]
        sourceOptions  // Expected to be in the form of [ { value: x, label: y } ]
    } = props;
    const [selectedIndex, setSelectedIndex] = React.useState<number | null>(null);
    const [editorOpen, setEditorOpen] = React.useState(false);
    const getTableContainer = () => document.getElementById("exportTable" + type);
    //region Functions
    const onSortStart = ({node, helper}: {node: any; helper: any}) => {
        node.childNodes.forEach((td: any, index: number) => {
            if (td.id === ("delete_" + type)) helper.childNodes[index].style.display = "none";
            helper.childNodes[index].style.width = `${td.offsetWidth}px`;
        });
    }
    const onItemSelect = (index: number) => {
        setSelectedIndex(index);
        setEditorOpen(true);
    }
    //endregion
    let source: string = selectedIndex !== null ? (items[selectedIndex].field || "") : "";
    let target: string = selectedIndex !== null ? (items[selectedIndex].alias || "") : "";
    if (type === "rules") {
        source = selectedIndex !== null ? (items[selectedIndex].SourceField || "") : "";
        target = selectedIndex !== null ? (items[selectedIndex].TargetField || "") : "";
    }
    return (
        <Grid item container style={{gap: pageContentGap}} flex={1}>
            <Table>
                <TableHead>
                    {headers}
                </TableHead>
                <SortableList
                    swapTargetSource={props.swapTargetSource}
                    helperClass={'tableDragElement'}
                    helperContainer={getTableContainer}
                    useDragHandle={props.disableOrdering || true}
                    disableOrdering={props.disableOrdering}
                    extraRows={props.extraRows}
                    rowActions={rowActions}
                    type={type}
                    items={items}
                    targetOptions={targetOptions}
                    sourceOptions={sourceOptions}
                    onConditionUpdate={onConditionUpdate}
                    onTargetUpdate={onTargetUpdate}
                    onSourceUpdate={onSourceUpdate}
                    onItemSelect={onItemSelect}
                    onItemDelete={(e: number) => {
                        setSelectedIndex(null);
                        onItemDelete(e);
                    }}
                    onSortEnd={onSortEnd}
                    onSortStart={onSortStart}
                />
            </Table>
            {actions}
            <EnhancedMappingEditor
                options={sourceOptions}
                condition={selectedIndex !== null ? items[selectedIndex].Condition : null}
                source={source}
                target={target}
                title={props.conditionaEditorTitle || "Edit Condition"}
                open={editorOpen}
                onClose={() => {
                    setSelectedIndex(null);
                    setEditorOpen(false);
                }}
                onChange={(values: any) => {
                    if (onConditionUpdate && selectedIndex !== null) onConditionUpdate(selectedIndex, values.Condition);
                    if (selectedIndex !== null) onTargetUpdate(selectedIndex, values.TargetField);
                    if (selectedIndex !== null) onSourceUpdate(selectedIndex, values.SourceField);
                }}
                beforeForm={() => {
                    return (
                        <FormSpy subscription={{values: true, pristine: true}}>
                            {({values, pristine}: any) => {
                                return (
                                    <>
                                        <Grid item container style={{gap: pageContentGap}}>
                                            <Field name={`TargetField`}
                                                   component={(props: any) => (
                                                       <EnhancedAutocomplete
                                                           containerStyle={{flex: 1}}
                                                           label={props.targetFieldTitle || "Output Column"}
                                                           notMultiple={true}
                                                           promptDisabled={true}
                                                           value={props.input.value || null}
                                                           options={targetOptions.map(option => option.value)}
                                                           onChange={(e: any, value: string) => {
                                                               props.input.onChange(value);
                                                           }}
                                                       />
                                                   )}/>
                                            <Field name={`SourceField`}
                                                   component={(props: any) => (
                                                       <EnhancedAutocomplete
                                                           containerStyle={{flex: 1}}
                                                           label={props.sourceFieldTitle || "Input Field/Expression"}
                                                           helperText='Please enclose constant values in quotes.'
                                                           notMultiple={true}
                                                           promptDisabled={true}
                                                           value={props.input.value || null}
                                                           options={sourceOptions.map(option => option.value)}
                                                           onChange={(e: any, value: string) => {
                                                               props.input.onChange(value);
                                                           }}
                                                       />
                                                   )}/>
                                        </Grid>
                                    </>
                                )
                            }}
                        </FormSpy>
                    )
                }}
            />
        </Grid>
    )
}
